import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import { useCreateGameMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { IGame, IPrinter } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import moment from "moment-timezone";

interface IGamepopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IGame;
}

const Coursing = ({
  openModel = false,
  handleCloseDialog,
  data,
}: IGamepopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newLocation, { isLoading }] = useCreateGameMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  
  const handleClose = () => {
    handleCloseDialog(false);
  };

  const formik = useFormik({
  initialValues: {
    id: data?.id ,
    name: data?.name || "",
    display_name: data?.display_name || "",
    short_description: data?.short_description || "",
    description: data?.description || "",
    pricing_type: data?.pricing_type || "",
    min_kids: data?.min_kids ?? "",      
    max_kids: data?.max_kids ?? "",
    min_adults: data?.min_adults ?? "",
    max_adults: data?.max_adults ?? "",
    free_kids_included: data?.free_kids_included ?? "",
    free_adults_included: data?.free_adults_included ?? "",
    extra_kids_allowed: data?.extra_kids_allowed ?? false,
    extra_adults_allowed: data?.extra_adults_allowed ?? false,
    kid_price: data?.kid_price ?? "",
    adult_price: data?.adult_price ?? "",
    extra_kid_price: data?.extra_kid_price ?? "",
    extra_adult_price: data?.extra_adult_price ?? "",
    shared_capacity: data?.shared_capacity ?? "",
    max_date_changes: data?.max_date_changes ?? "",
    refund_after_changes: data?.refund_after_changes ?? false,
    isActive: data?.isActive ? Boolean(data?.isActive) : false,
    viewOnline: data?.viewOnline ? Boolean(data?.viewOnline) : false,
    viewPos: data?.viewPos ? Boolean(data?.viewPos) : false,

    },
    onSubmit: async (values) => {
      try {
       
         const temData = {
  id: values?.id,
  name: values.name,
  display_name: values.display_name,
  short_description: values.short_description,
  description: values.description,
  pricing_type: values.pricing_type,
  min_kids: Number(values.min_kids) || 0,
  max_kids: Number(values.max_kids) || 0,
  min_adults: Number(values.min_adults) || 0,
  max_adults: Number(values.max_adults) || 0,
  free_kids_included: Number(values.free_kids_included) || 0,
  free_adults_included: Number(values.free_adults_included) || 0,
  extra_kids_allowed: values.extra_kids_allowed,
  extra_adults_allowed: values.extra_adults_allowed,
  kid_price: Number(values.kid_price) || 0.0,
  adult_price: Number(values.adult_price) || 0.0,
  extra_kid_price: Number(values.extra_kid_price) || 0.0,
  extra_adult_price: Number(values.extra_adult_price) || 0.0,
  shared_capacity: values.shared_capacity ? Number(values.shared_capacity) : null,
  max_date_changes: Number(values.max_date_changes) || 0,
  refund_after_changes: Boolean(values.refund_after_changes),
  isActive: values.isActive,
  viewOnline: values.viewOnline,
  viewPos: values.viewPos,
};
    
        if (!data) {
          delete values.id;
        }

        const addCompanyResponse = await newLocation(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
         // console.log((addCompanyResponse.data as IGame).id);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });


    const formValid = useMemo(() => {
        return formik.values.name === "" ||
          formik.values.name === undefined
          ? false
          : true;
      }, [formik]);
    

  const gridHeight = isMobile ? "30px" : "400px";

  return (
    <Dialog
      open={open}
      PaperProps={{
        style: {
          maxWidth: "1700px",
        },
      }}
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={3} md={3} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Edit Game
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={9} md={9} sm={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Game Information
                        </Typography>
                      </Grid>
                    </Grid>

                               <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                             Name <span style={{ color: "red" }}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Name"
                            size="small"
                            {...formik.getFieldProps("name")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                   
<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Display Name</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Display Name"
        size="small"
        {...formik.getFieldProps("display_name")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={12} md={12} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Short Description</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Short Description"
        size="small"
        multiline
        rows={2}
        {...formik.getFieldProps("short_description")}
        className={classes.discriptionTextField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={12} md={12} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Description</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Description"
        size="small"
        multiline
        rows={4}
        {...formik.getFieldProps("description")}
        className={classes.discriptionTextField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Pricing Type <span style={{ color: "red" }}>*</span></Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        select
        fullWidth
        size="small"
        value={formik.values.pricing_type}
        onChange={(e) => formik.setFieldValue("pricing_type", e.target.value)}
      >
        <MenuItem value="">Select Type</MenuItem>
        <MenuItem value="per_person">Per Person</MenuItem>
        <MenuItem value="per_unit">Per Unit</MenuItem>
        <MenuItem value="fixed">Fixed</MenuItem>
      </TextField>
    </Grid>
  </Grid>
</Grid>

{/* <Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Shared Capacity</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        placeholder="Shared Capacity"
        size="small"
        {...formik.getFieldProps("shared_capacity")}
        className={classes.textField}
        inputProps={{ min: "0" }}
      />
    </Grid>
  </Grid>
</Grid> */}



<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Date Changes</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_date_changes")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
                   

                    

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Active</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <CustomSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            formik={formik}
                            name="isActive"
                          />
                        </Grid>
                      </Grid>
                    </Grid>


 <Grid item lg={4} md={4} sm={3} xs={6}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                               View POS 
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              formik={formik}
                              name="viewOnline"
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                        <Grid item lg={4} md={4} sm={3} xs={6}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              View Online
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              formik={formik}
                              name="viewPos"
                            />
                          </Grid>
                        </Grid>
                      </Grid>




                  </Grid>
                </Card>
              </Grid>



<Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                        Kids Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                    

                  
<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Min Kids</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("min_kids")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Kids</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_kids")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Free Kids Included</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("free_kids_included")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Kids Allowed</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <CustomSwitch
        formik={formik}
        name="extra_kids_allowed"
        color="primary"
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Kid Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("kid_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Kid Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("extra_kid_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

           



                     
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                        Adults Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                    

                  
<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Min Adults</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("min_adults")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Adults</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_adults")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
                      


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Free Adults Included</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("free_adults_included")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Adults Allowed</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <CustomSwitch
        formik={formik}
        name="extra_adults_allowed"
        color="primary"
      />
    </Grid>
  </Grid>
</Grid>


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Adult Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("adult_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Adult Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("extra_adult_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
           
                     
                    </Grid>
                  </Card>
                </Grid>


                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<SaveAltIcon />}
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Coursing;
